import { ElementRef, useCallback, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import TagInput from './Tag';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import { usePageTitleMutation } from '@/features/pages/hooks/usePageTitleMutation';
import { Page } from '@/features/pages/types/page.types';

type TitleProp = {
  page: Page;
  showTag?: boolean;
  enableEditing?: boolean;
};

const PageTitle: React.FC<TitleProp> = ({ page, showTag = true, enableEditing = true }) => {
  const title = page.title || 'Untitled';
  const inputRef = useRef<ElementRef<'textarea'>>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [value, setValue] = useState(title);
  const editable = usePageStore(state => state.editable);

  const updatePageTitle = usePageTitleMutation();

  const enableInput = useCallback(() => {
    if (!editable) {
      return;
    }
    setValue(title);
    setIsEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, [editable, title]);

  const disableInput = () => {
    setIsEditing(false);
  };

  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      disableInput();
    }
  }, []);

  const onInput = useCallback(
    (value: string) => {
      setValue(value);
      void updatePageTitle(page.id, value || 'Untitled');
    },
    [page.id, updatePageTitle]
  );

  return (
    <div className="w-full border-none min-h-16">
      {showTag && <TagInput page={page} editable={editable} />}
      <div className="mx-4 md:mx-12">
        {isEditing && enableEditing && editable ? (
          <TextareaAutosize
            ref={inputRef}
            spellCheck="false"
            onBlur={disableInput}
            onKeyDown={onKeyDown}
            placeholder={value}
            value={value === 'Untitled' ? '' : value}
            onChange={e => {
              onInput(e.target.value);
            }}
            className="resize-none overflow-hidden break-words bg-transparent text-3xl md:text-4xl font-bold outline-none w-full"
          />
        ) : (
          <div
            onClick={enableInput}
            className="break-words text-3xl md:text-4xl font-bold outline-none pb-[8.25px]"
          >
            {title}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageTitle;
